import { createSlice, PayloadAction, Dispatch } from '@reduxjs/toolkit';
import { APIError } from 'models';
import {
  DeviceFilter,
  DevicesInRolloutSuccess,
  GroupsInRolloutSuccess,
  Rollout,
  RolloutFilter,
  RolloutState,
  RolloutSuccess,
  RolloutsSuccess,
} from 'models/rollout';
import { FieldValues } from 'react-hook-form';
import {
  cancelRolloutById,
  createRollout,
  findRollouts,
  getDevicesInRolloutByFilter,
  getGroupsInRolloutByRolloutId,
  getRolloutById,
  startRolloutById,
} from 'services/rolloutService/rolloutService';

export const defaultState: RolloutState = {
  list: {
    data: null,
    totalCount: 0,
    isLoading: false,
    error: null,
    filters: {
      active: false,
      data: null,
    },
  },
  rolloutDetail: {
    data: null,
    isLoading: false,
    error: null,
    devices: {
      data: null,
      totalCount: 0,
      filters: {
        data: null,
        active: false,
      },
      isLoading: false,
      error: null,
    },
    groups: {
      data: null,
      totalCount: 0,
      isLoading: false,
      error: null,
    },
  },
};
// Export slice
export const rolloutSlice = createSlice({
  name: 'rollout',
  initialState: defaultState,
  reducers: {
    getRollouts: (state) => {
      state.list.isLoading = true;
      state.list.error = null;
    },
    createRolloutSuccess: (state, action: PayloadAction<Rollout>) => {
      state.rolloutDetail.error = null;
      state.rolloutDetail.data = action.payload;
    },
    getRolloutsSuccess: (state, action: PayloadAction<RolloutsSuccess>) => {
      state.list.isLoading = false;
      state.list.error = null;
      state.list.data = action.payload.response;
      state.list.totalCount = action.payload.totalCount;
      state.list.filters.data = action.payload.filters;
    },
    getRolloutsFailure: (state, action: PayloadAction<APIError>) => {
      state.list.data = [];
      state.list.totalCount = 0;
      state.list.isLoading = false;
      state.list.error = action.payload;
    },
    setRolloutFilters: (state, action: PayloadAction<boolean>) => {
      state.list.filters.active = action.payload;
    },
    creatingRollout: (state) => {
      state.rolloutDetail.isLoading = true;
      state.rolloutDetail.error = null;
    },
    createRolloutFailure: (state, action: PayloadAction<APIError>) => {
      state.rolloutDetail.data = null;
      state.rolloutDetail.isLoading = false;
      state.rolloutDetail.error = action.payload;
    },
    startingGetRollout: (state) => {
      state.list.isLoading = true;
      state.list.error = null;
      state.rolloutDetail.isLoading = true;
      state.rolloutDetail.error = null;
    },
    getRolloutSuccess: (state, action: PayloadAction<RolloutSuccess>) => {
      state.list.isLoading = false;
      const fetchedRollout = action.payload.response;
      const currentRolloutIndex = state.list.data?.findIndex(
        (rollout) => rollout.id === fetchedRollout.id
      );
      if (state.list.data && currentRolloutIndex !== undefined) {
        state.list.data[currentRolloutIndex] = fetchedRollout;
      }
      state.rolloutDetail.isLoading = false;
      state.rolloutDetail.data = fetchedRollout;
    },
    getRolloutFailure: (state, action: PayloadAction<APIError>) => {
      state.list.data = null;
      state.list.isLoading = false;
      state.list.error = action.payload;
      state.rolloutDetail.data = null;
      state.rolloutDetail.isLoading = false;
      state.rolloutDetail.error = action.payload;
    },
    getDevicesInRollout: (state) => {
      state.rolloutDetail.devices.isLoading = true;
      state.rolloutDetail.devices.error = null;
    },
    getDevicesInRolloutSuccess: (
      state,
      action: PayloadAction<DevicesInRolloutSuccess>
    ) => {
      state.rolloutDetail.devices.isLoading = false;
      state.rolloutDetail.devices.filters.data =
        action.payload.response.filters;
      state.rolloutDetail.devices.data = action.payload.response.devices.map(
        (device) => device.attributes
      );
      state.rolloutDetail.devices.totalCount =
        action.payload.response.totalCount;
    },
    getDevicesInRolloutFailure: (state, action: PayloadAction<APIError>) => {
      state.rolloutDetail.devices.data = null;
      state.rolloutDetail.devices.totalCount = 0;
      state.rolloutDetail.devices.isLoading = false;
      state.rolloutDetail.devices.error = action.payload;
    },
    setDeviceFilters: (state, action: PayloadAction<boolean>) => {
      state.rolloutDetail.devices.filters.active = action.payload;
    },
    getGroupsInRollout: (state) => {
      state.rolloutDetail.groups.isLoading = true;
      state.rolloutDetail.groups.error = null;
    },
    getGroupsInRolloutSuccess: (
      state,
      action: PayloadAction<GroupsInRolloutSuccess>
    ) => {
      state.rolloutDetail.groups.data = action.payload.response.groups.map(
        (group) => ({
          id: group.attributes.groupId,
          name: group.attributes.name,
          progress: group.attributes.progress,
          status: group.attributes.status,
          totalTargets: group.attributes.totalTargets,
        })
      );
      state.rolloutDetail.groups.totalCount =
        action.payload.response.totalCount;
      state.rolloutDetail.groups.isLoading = false;
    },
    getGroupsInRolloutFailure: (state, action: PayloadAction<APIError>) => {
      state.rolloutDetail.groups.data = null;
      state.rolloutDetail.groups.totalCount = 0;
      state.rolloutDetail.groups.isLoading = false;
      state.rolloutDetail.groups.error = action.payload;
    },
  },
});

// Export actions
export const {
  getRollouts,
  getRolloutsSuccess,
  getRolloutsFailure,
  setRolloutFilters,
  creatingRollout,
  createRolloutFailure,
  createRolloutSuccess,
  startingGetRollout,
  getRolloutSuccess,
  getRolloutFailure,
  getDevicesInRollout,
  getDevicesInRolloutSuccess,
  getDevicesInRolloutFailure,
  setDeviceFilters,
  getGroupsInRollout,
  getGroupsInRolloutSuccess,
  getGroupsInRolloutFailure,
} = rolloutSlice.actions;

// Export reducer
export const rolloutReducer = rolloutSlice.reducer;

export const rolloutListSelector = (state: any) => state.rollout.list;
export const createRolloutSelector = (state: any) =>
  state.rollout.rolloutDetail;
export const rolloutDetailsSelector = (state: any) =>
  state.rollout.rolloutDetail;

// Export thunk
export const fetchRolloutList =
  /* prettier-ignore */
  (filter: RolloutFilter[], offset: number, limit: number = 10) => async (dispatch: Dispatch) => {
    dispatch(getRollouts());
    try {
      const response = await findRollouts(offset, limit, filter);
      dispatch(
        getRolloutsSuccess({
          response: response.data,
          totalCount: response.meta.totalCount,
          filters: response.data.length ? filter : null,
        })
      );
    } catch (e: any) {
      dispatch(getRolloutsFailure(e));
    }
  };

export const storeRollout =
  (rollout: FieldValues, softwareId: string) => async (dispatch: Dispatch) => {
    dispatch(creatingRollout());
    try {
      const response = await createRollout({
        data: {
          attributes: {
            name: rollout.name,
            softwareId: softwareId,
            pnc: rollout.pnc,
            excludeTestDevices: rollout.excludeTestDevices,
          },
        },
      });
      dispatch(createRolloutSuccess(response));
    } catch (e: any) {
      dispatch(createRolloutFailure(e));
    }
  };

export const startRollout =
  (rolloutId: string) => async (dispatch: Dispatch) => {
    dispatch(startingGetRollout());
    try {
      const response = await startRolloutById(rolloutId);
      dispatch(
        getRolloutSuccess({
          response: response.data,
        })
      );
    } catch (e: any) {
      dispatch(getRolloutFailure(e));
    }
  };

export const cancelRollout =
  (rolloutId: string, offset: number) => async (dispatch: Dispatch) => {
    dispatch(startingGetRollout());
    try {
      const response = await cancelRolloutById(rolloutId, offset);
      dispatch(
        getRolloutSuccess({
          response: response.data,
        })
      );
    } catch (e: any) {
      dispatch(getRolloutFailure(e));
    }
  };

export const getRollout = (rolloutId: string) => async (dispatch: Dispatch) => {
  dispatch(startingGetRollout());
  try {
    const response = await getRolloutById(rolloutId);
    dispatch(
      getRolloutSuccess({
        response: response.data,
      })
    );
  } catch (e: any) {
    dispatch(getRolloutFailure(e));
  }
};

export const fetchGroupsInRollout =
  /* prettier-ignore */
  (rolloutId: string, offset: number | 0) => async (dispatch: Dispatch) => {
    dispatch(getGroupsInRollout());
    try {
      const limit = 10;
      const response = await getGroupsInRolloutByRolloutId(
        rolloutId,
        offset,
        limit,
      );
      dispatch(
        getGroupsInRolloutSuccess({
          response: {
            groups: response.data,
            totalCount: response.meta.totalCount,
          }
        })
      );
    } catch (e: any) {
      dispatch(getGroupsInRolloutFailure(e));
    }
  };

export const fetchDevicesInRollout =
  /* prettier-ignore */
  (rolloutId: string, deviceFilters: DeviceFilter[], offset: number | 0) => async (dispatch: Dispatch) => {
    dispatch(getDevicesInRollout());
    try {
      const limit = 10;
      const response = await getDevicesInRolloutByFilter(
        rolloutId,
        deviceFilters,
        offset,
        limit,
      );
      dispatch(
        getDevicesInRolloutSuccess({
          response: {
            devices: response.data,
            totalCount: response.meta.totalCount,
            filters: deviceFilters,
          }
        })
      );
    } catch (e: any) {
      dispatch(getDevicesInRolloutFailure(e));
    }
  };
